/**
 * Owner-only Channel Categories editor.
 *
 * Lets the owner add, rename, reorder, and delete the Sky Q-style rails
 * the channel guide groups channels by. Each category carries a free-text
 * list of keywords used to auto-detect a channel's rail when the owner
 * hasn't pinned one explicitly (the guide's CATEGORY dropdown). Closing
 * the panel broadcasts `crt:categories-updated` so any open ChannelGuide
 * mount re-fetches without a reload.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OwnerCategoriesPanel({ open, onClose, getToken, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Pending edits live in `drafts` keyed by id so the input fields stay
  // responsive without forcing a full save on every keystroke.
  const [drafts, setDrafts] = useState({});
  const [newName, setNewName] = useState("");
  const [newKeywords, setNewKeywords] = useState("");

  const authHeaders = () => {
    const t = getToken && getToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchCats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/categories`);
      setRows(res.data || []);
      const d = {};
      for (const c of res.data || []) {
        d[c.id] = { name: c.name, keywords: (c.keywords || []).join(", ") };
      }
      setDrafts(d);
    } catch (e) {
      toast.error("LOAD FAILED", { description: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchCats();
  }, [open]);

  if (!open) return null;

  const move = async (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= rows.length) return;
    const next = rows.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    setRows(next);
    setBusy(true);
    try {
      await axios.post(
        `${API}/categories/reorder`,
        { category_ids: next.map((r) => r.id) },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );
      if (onChanged) onChanged();
    } catch (e) {
      toast.error("REORDER FAILED", { description: e.response?.data?.detail || e.message });
      fetchCats();
    } finally {
      setBusy(false);
    }
  };

  const save = async (cat) => {
    const draft = drafts[cat.id] || {};
    const name = (draft.name || "").trim();
    const keywords = (draft.keywords || "").split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    if (!name) {
      toast.error("NAME REQUIRED");
      return;
    }
    setBusy(true);
    try {
      await axios.patch(
        `${API}/categories/${cat.id}`,
        { name, keywords },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );
      toast.success("SAVED");
      fetchCats();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error("SAVE FAILED", { description: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (cat) => {
    if (!window.confirm(`Remove category "${cat.name}"? Channels pinned here will fall back to auto-detect.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/categories/${cat.id}`, { headers: authHeaders() });
      toast.success("CATEGORY REMOVED");
      fetchCats();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error("REMOVE FAILED", { description: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error("NAME REQUIRED");
      return;
    }
    const keywords = newKeywords.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    setBusy(true);
    try {
      await axios.post(
        `${API}/categories`,
        { name, keywords },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );
      toast.success("CATEGORY ADDED");
      setNewName("");
      setNewKeywords("");
      fetchCats();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error("ADD FAILED", { description: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="absolute top-12 right-0 w-[420px] max-h-[80vh] overflow-y-auto bg-black border-2 border-[var(--phosphor)] p-4 text-[var(--phosphor)] shadow-[0_0_20px_color-mix(in srgb, var(--phosphor) 35%, transparent)] scrollbar-crt"
      data-testid="categories-panel"
      style={{ fontFamily: "VT323, monospace" }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs tracking-[0.3em]">┌─ ★ CATEGORIES ─┐</div>
        <button onClick={onClose} data-testid="categories-panel-close" className="text-[var(--phosphor)]">
          <X size={14} />
        </button>
      </div>
      <div className="text-[10px] opacity-70 mb-3 leading-snug">
        Categories become the rails (rows) inside the channel guide. Keywords auto-place new channels into the right rail based on their name — owner-pinned categories always win.
      </div>

      {loading ? (
        <div className="text-center opacity-60 py-4 text-[11px]">LOADING…</div>
      ) : rows.length === 0 ? (
        <div className="text-center opacity-60 py-4 text-[11px]">— NO CATEGORIES —</div>
      ) : (
        <div className="flex flex-col gap-2 mb-3">
          {rows.map((cat, i) => (
            <div
              key={cat.id}
              className="border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] p-2 flex flex-col gap-1.5"
              data-testid={`category-row-${cat.id}`}
            >
              <div className="flex items-center gap-1.5">
                <div className="flex flex-col gap-0 shrink-0">
                  <button
                    type="button"
                    onClick={() => move(i, -1)}
                    disabled={busy || i === 0}
                    data-testid={`category-up-${cat.id}`}
                    title="Move up"
                    className="text-[color-mix(in_srgb,var(--phosphor)_70%,transparent)] hover:text-[var(--phosphor)] disabled:opacity-25 leading-none p-0"
                  >
                    <ArrowUp size={11} />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, 1)}
                    disabled={busy || i === rows.length - 1}
                    data-testid={`category-down-${cat.id}`}
                    title="Move down"
                    className="text-[color-mix(in_srgb,var(--phosphor)_70%,transparent)] hover:text-[var(--phosphor)] disabled:opacity-25 leading-none p-0"
                  >
                    <ArrowDown size={11} />
                  </button>
                </div>
                <input
                  type="text"
                  value={drafts[cat.id]?.name ?? cat.name}
                  onChange={(e) => setDrafts((d) => ({ ...d, [cat.id]: { ...(d[cat.id] || {}), name: e.target.value } }))}
                  maxLength={48}
                  data-testid={`category-name-${cat.id}`}
                  className="flex-1 bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] text-[var(--phosphor)] p-1 outline-none text-[11px] tracking-[0.18em] uppercase"
                />
                <button
                  type="button"
                  onClick={() => save(cat)}
                  disabled={busy}
                  data-testid={`category-save-${cat.id}`}
                  title="Save"
                  className="p-1 border border-[var(--phosphor)] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40"
                >
                  <Save size={11} />
                </button>
                <button
                  type="button"
                  onClick={() => remove(cat)}
                  disabled={busy}
                  data-testid={`category-delete-${cat.id}`}
                  title="Delete"
                  className="p-1 border border-[#FF3B30]/40 text-[#FF3B30] hover:border-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors disabled:opacity-40"
                >
                  <Trash2 size={11} />
                </button>
              </div>
              <div>
                <div className="text-[9px] tracking-[0.25em] opacity-70 mb-0.5">KEYWORDS (comma-sep)</div>
                <input
                  type="text"
                  value={drafts[cat.id]?.keywords ?? (cat.keywords || []).join(", ")}
                  onChange={(e) => setDrafts((d) => ({ ...d, [cat.id]: { ...(d[cat.id] || {}), keywords: e.target.value } }))}
                  placeholder="movie, film, cinema"
                  data-testid={`category-keywords-${cat.id}`}
                  className="w-full bg-black border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] text-[var(--phosphor)] p-1 outline-none text-[10px] placeholder:text-[color-mix(in_srgb,var(--phosphor)_30%,transparent)]"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add new category row */}
      <div className="border border-dashed border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-2 flex flex-col gap-1.5">
        <div className="text-[10px] tracking-[0.25em] opacity-80">+ ADD CATEGORY</div>
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="e.g. SPORTS"
          maxLength={48}
          data-testid="category-new-name"
          className="bg-black border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] text-[var(--phosphor)] p-1 outline-none text-[11px] tracking-[0.18em] uppercase placeholder:text-[color-mix(in_srgb,var(--phosphor)_30%,transparent)]"
        />
        <input
          type="text"
          value={newKeywords}
          onChange={(e) => setNewKeywords(e.target.value)}
          placeholder="football, soccer, sport"
          data-testid="category-new-keywords"
          className="bg-black border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] text-[var(--phosphor)] p-1 outline-none text-[10px] placeholder:text-[color-mix(in_srgb,var(--phosphor)_30%,transparent)]"
        />
        <button
          type="button"
          onClick={add}
          disabled={busy || !newName.trim()}
          data-testid="category-new-add"
          className="flex items-center justify-center gap-1 border border-[var(--phosphor)] py-1 text-[10px] tracking-widest hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40"
        >
          <Plus size={11} />
          ADD
        </button>
      </div>
    </div>
  );
}
